/* @jsx h */
import { defineComponent, h } from "vue";
import type { MenuCreatorCategory, MenuCreatorProduct } from "../types";
import { NutritionalLabel } from "./NutritionalLabel";
import { useMenu } from "@app/vue/providers/index";

export interface PreviewMenuComponentProps {
  categories: MenuCreatorCategory[];
  onShowProductDetails: (product: MenuCreatorProduct) => void;
}

export const PreviewMenuComponent = defineComponent<PreviewMenuComponentProps>({
  name: "PreviewMenuComponent",
  props: ["categories", "onShowProductDetails"],
  setup(props) {
    const { shop } = useMenu();
    const categories = () => props.categories ?? [];

    return () => (
      <div class="space-y-12">
        <div class="text-center mb-12">
          <h2 class="text-4xl font-serif font-light text-gray-900 mb-6">
            Menu Preview
          </h2>
          <p class="text-lg text-gray-600 font-light max-w-2xl mx-auto">
            Experience your menu as your guests will see it
          </p>
        </div>

        {categories().length === 0 ? (
          <div class="text-center py-24 bg-white rounded-2xl shadow-lg">
            <div class="text-8xl mb-8 text-gray-300">📋</div>
            <h3 class="text-2xl font-serif font-light text-gray-900 mb-4">
              No menu items to preview
            </h3>
            <p class="text-gray-600 font-light text-lg">
              Switch to Edit Mode to start building your menu
            </p>
          </div>
        ) : (
          <div class="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div class="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-12 text-center">
              <h1 class="text-4xl font-serif font-light mb-4">Our Menu</h1>
              <p class="text-gray-300 font-light text-lg">
                Curated with passion, served with excellence
              </p>
            </div>

            <div class="p-12">
              {categories().map((category, categoryIndex) => (
                <div key={category.id} class={categoryIndex > 0 ? "mt-16" : ""}>
                  <div class="text-center mb-12">
                    <div class="flex items-center justify-center gap-4 mb-4">
                      <div class="h-px bg-gradient-to-r from-transparent via-gray-300 to-gray-400 flex-1 max-w-24"></div>
                      <div
                        class="w-4 h-4 rounded-full shadow-sm"
                        style={`background-color: ${category.color}`}
                      ></div>
                      <div class="h-px bg-gradient-to-r from-gray-400 via-gray-300 to-transparent flex-1 max-w-24"></div>
                    </div>
                    <h3 class="text-3xl font-serif font-light text-gray-900 mb-2">
                      {category.name}
                    </h3>
                  </div>

                  <div class="space-y-8">
                    {category.products.map((product) => (
                      <div
                        key={product.id}
                        class="group cursor-pointer border-b border-gray-100 pb-8 last:border-b-0 hover:bg-gray-50 p-6 rounded-lg transition-colors"
                        onClick={() => props.onShowProductDetails(product)}
                      >
                        <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
                          <div class="lg:col-span-1">
                            <div class="aspect-square rounded-lg overflow-hidden bg-gray-100 relative">
                              <img
                                src={
                                  product.sample_image ||
                                  "https://images.unsplash.com/photo-1551218808-94e220e084d2?w=400&h=400&fit=crop"
                                }
                                alt={product.name}
                                class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                          </div>

                          <div class="lg:col-span-2 space-y-4">
                            <div>
                              <h4 class="text-2xl font-serif font-medium text-gray-900">
                                {product.name}
                              </h4>
                              <p class="text-gray-600 font-light mt-2 leading-relaxed">
                                {product.description}
                              </p>
                            </div>
                            <div class="flex flex-wrap gap-2">
                              {product.nutritional_info?.dietaryTags?.map(
                                (tag) => (
                                  <span
                                    key={tag}
                                    class="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium"
                                  >
                                    {tag}
                                  </span>
                                ),
                              )}
                              {product.nutritional_info?.allergens?.map(
                                (allergen) => (
                                  <span
                                    key={allergen}
                                    class="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium"
                                  >
                                    Contains {allergen}
                                  </span>
                                ),
                              )}
                            </div>
                            <div class="text-2xl font-semibold text-gray-900 mb-2 py-4 px-2">
                              {shop.value.currency} {product.price}
                            </div>
                          </div>

                          <div class="lg:col-span-1 text-right lg:text-right">
                            {product.nutritional_info && (
                              <NutritionalLabel
                                nutrition={product.nutritional_info}
                                variant="compact"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  },
});
